import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  type DataGridProps,
  type GridPaginationModel,
  type GridRowIdGetter,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import { EmptyState, ErrorState } from '../feedback/States';

export interface DataTableProps<R extends GridValidRowModel> extends Omit<
  DataGridProps<R>,
  'rows' | 'columns' | 'slots'
> {
  rows: R[];
  columns: DataGridProps<R>['columns'];
  getRowId?: GridRowIdGetter<R>;
  /** Renders the search box in the toolbar; omit to hide it. */
  search?: { value: string; onChange: (value: string) => void; placeholder?: string };
  /** Extra toolbar content, e.g. status filters or a "New" button. */
  toolbarActions?: ReactNode;
  /** Shown when there are no rows and not loading. */
  empty?: EmptyConfig;
  error?: { message?: string; onRetry?: () => void };
  /** Adds the CSV/print export button. Off by default — some data is sensitive. */
  allowExport?: boolean;
  height?: number | string;
}

interface SearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface EmptyConfig {
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

/** Module scope for the same reason as the toolbar: a new identity remounts it. */
function NoRows({ empty }: { empty?: EmptyConfig }) {
  return (
    <EmptyState
      title={empty?.title ?? 'Nothing to show'}
      description={empty?.description}
      action={empty?.action}
    />
  );
}

interface DataTableToolbarProps {
  search?: SearchConfig;
  toolbarActions?: ReactNode;
  allowExport: boolean;
  dividerColor: string;
}

// MUI types slot props by declaration merging, so `slotProps.toolbar` rejects
// anything it has not been told about. Declaring them here is what lets the
// changing values reach the toolbar as PROPS rather than as a new component.
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    search?: SearchConfig;
    toolbarActions?: ReactNode;
    allowExport: boolean;
    dividerColor: string;
  }
  interface NoRowsOverlayPropsOverrides {
    empty?: EmptyConfig;
  }
}

/**
 * Declared at MODULE scope, deliberately.
 *
 * MUI remounts a slot whenever the component passed to `slots` has a different
 * identity. This toolbar used to be built by a `useMemo` keyed on `[search,
 * toolbarActions]`, and every caller passes `search={{ value, onChange }}` — a
 * fresh object literal on each render. So one keystroke changed `search`,
 * produced a new component, and React tore the toolbar down and built it again:
 * the TextField was destroyed mid-typing and the caret went with it. That is the
 * "I have to click the box again after every character" bug, on every page with
 * a search field.
 *
 * Changing values travel through `slotProps.toolbar` instead, which is a props
 * update rather than a new component type, so the input keeps its focus.
 */
function DataTableToolbar({
  search,
  toolbarActions,
  allowExport,
  dividerColor,
}: DataTableToolbarProps) {
  const hasControls = Boolean(search) || Boolean(toolbarActions);
  return (
    <GridToolbarContainer
      sx={{
        p: 1.5,
        gap: 1,
        flexWrap: 'wrap',
        borderBottom: hasControls ? `1px solid ${dividerColor}` : 'none',
      }}
    >
      {search && (
        <TextField
          value={search.value}
          onChange={e => search.onChange(e.target.value)}
          placeholder={search.placeholder ?? 'Search…'}
          size="small"
          sx={{ maxWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      {toolbarActions}
      <Box sx={{ flex: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      {allowExport && <GridToolbarExport />}
    </GridToolbarContainer>
  );
}

/**
 * The one table in the portal.
 *
 * The four legacy apps each hand-rolled a table plus its own pagination
 * component, with different empty states, different sort behaviour and
 * different (or missing) column controls. This replaces all of them.
 */
export function DataTable<R extends GridValidRowModel>({
  rows,
  columns,
  getRowId,
  search,
  toolbarActions,
  empty,
  error,
  allowExport = false,
  height = 560,
  loading,
  ...gridProps
}: DataTableProps<R>) {
  const theme = useTheme();

  // Searching while on a later page would otherwise leave the grid on that
  // page of a now-shorter result set — the user sees an empty table and
  // concludes the search only looked at the first page. Go back to page 1
  // whenever the search text changes.
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  useEffect(() => {
    setPagination(p => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [search?.value]);

  if (error) {
    return <ErrorState message={error.message} onRetry={error.onRetry} />;
  }

  return (
    <Stack sx={{ height, width: '100%' }}>
      <DataGrid<R>
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        slots={{ toolbar: DataTableToolbar, noRowsOverlay: NoRows }}
        slotProps={{
          toolbar: {
            search,
            toolbarActions,
            allowExport,
            dividerColor: theme.palette.divider,
          },
          noRowsOverlay: { empty },
        }}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.custom.radius.lg}px`,
          bgcolor: 'background.paper',
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: theme.palette.mode === 'light' ? theme.custom.neutral[50] : undefined,
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '0.8125rem' },
          '& .MuiDataGrid-cell': { fontSize: '0.8125rem' },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
          '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
          '& .MuiDataGrid-overlayWrapper': { minHeight: 220 },
        }}
        {...gridProps}
      />
    </Stack>
  );
}
